import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { finalize } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LoaderUtilService } from '@app/services/loader/loader-util.service';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { BYPASS_LOADER } from '@app/api-v2/util/http/http-client.service';
import { environment } from '@src/environments/environment';

// Configuration constants
const LOADER_CONFIG = {
  MIN_LOADING_TIME: 300, // Minimum time to show loader (prevents flickering)
  LOADER_DELAY: 200, // Delay before showing loader for quick requests
  MAX_CONCURRENT_REQUESTS: 10, // Prevent too many simultaneous requests
  EXCLUDED_METHODS: ['HEAD', 'OPTIONS'] as const,
  EXCLUDED_URL_PATTERNS: [
    '/assets/',
    '/favicon',
    '.css',
    '.js',
    '.png',
    '.jpg',
    '.svg',
    'analytics',
    'tracking',
  ] as const,
} as const;

// Global state management
let activeRequestCount = 0;
let loaderStartTime: number | null = null;
let loaderTimeout: any = null;

export const HttpLoaderInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const deviceService = inject(B2bDeviceUtilService);

  if (deviceService?.isServer()) {
    return next(req);
  }

  const loaderService = inject(LoaderUtilService);
  const shouldByPass: boolean = shouldBypassLoader(req, deviceService);

  // Early return conditions
  if (shouldByPass) {
    return next(req);
  }

  // Increment active request counter
  activeRequestCount++;

  // Show loader with debouncing
  showLoaderWithDelay(loaderService);

  const startTime = Date.now();

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        logRequestCompletion(req, startTime, 'success');
      }
    }),
    catchError((error: HttpErrorResponse) => {
      logRequestCompletion(req, startTime, 'error', error);
      throw error;
    }),
    finalize(() => {
      activeRequestCount = Math.max(0, activeRequestCount - 1);
      hideLoaderWithDelay(loaderService);
    }),
  );
};

/**
 * Determines if loader should be bypassed for this request
 */
const shouldBypassLoader = (
  req: HttpRequest<unknown>,
  deviceService: B2bDeviceUtilService,
): boolean => {
  // Check bypass context
  if (req.context.get(BYPASS_LOADER) === true) {
    return true;
  }

  // Skip loader for desktop users (assuming they have better performance)
  // TODO commented desktop disable loader code
  /*if (deviceService?.isDesktop?.()) {
    return true;
  }*/

  // Skip for certain HTTP methods
  if (LOADER_CONFIG.EXCLUDED_METHODS.includes(req.method as any)) {
    return true;
  }

  // Skip for static assets and certain URL patterns
  if (
    LOADER_CONFIG.EXCLUDED_URL_PATTERNS.some((pattern) =>
      req.url.toLowerCase().includes(pattern),
    )
  ) {
    return true;
  }

  // Skip if too many concurrent requests (prevent overwhelming UI)
  if (activeRequestCount >= LOADER_CONFIG.MAX_CONCURRENT_REQUESTS) {
    return true;
  }

  return false;
};

/**
 * Shows loader with intelligent debouncing
 */
const showLoaderWithDelay = (loaderService: LoaderUtilService): void => {
  // Only show loader if this is the first active request
  if (activeRequestCount === 1) {
    // Clear any existing timeout
    if (loaderTimeout) {
      clearTimeout(loaderTimeout);
    }

    // Show loader after a small delay to prevent flickering for quick requests
    loaderTimeout = setTimeout(() => {
      try {
        if (activeRequestCount > 0) {
          // Double-check we still need the loader
          loaderService.show('Loading...', {
            backdrop: true,
          });
          loaderStartTime = Date.now();
        }
      } catch (error) {
        console.error('Error showing loader:', error);
      }
    }, LOADER_CONFIG.LOADER_DELAY);
  }
};

/**
 * Hides loader with minimum display time consideration
 */
const hideLoaderWithDelay = (loaderService: LoaderUtilService): void => {
  // Only hide loader when all requests are complete
  if (activeRequestCount === 0) {
    // Clear any pending show timeout
    if (loaderTimeout) {
      clearTimeout(loaderTimeout);
      loaderTimeout = null;
    }

    const hideLoader = () => {
      try {
        loaderService.hide();
        loaderStartTime = null;
      } catch (error) {
        console.error(
          'Inside HttpLoaderInterceptor: Error hiding loader:',
          error,
        );
      }
    };

    // If loader was shown, ensure minimum display time
    if (loaderStartTime) {
      const elapsedTime = Date.now() - loaderStartTime;
      const remainingTime = LOADER_CONFIG.MIN_LOADING_TIME - elapsedTime;
      if (remainingTime > 0) {
        setTimeout(hideLoader, remainingTime);
      } else {
        hideLoader();
      }
    } else {
      // Loader wasn't shown yet, just clear the timeout
      hideLoader();
    }
  }
};

/**
 * Logs request completion for monitoring and debugging
 */
const logRequestCompletion = (
  req: HttpRequest<unknown>,
  startTime: number,
  status: 'success' | 'error',
  error?: HttpErrorResponse,
): void => {
  if (!environment?.production) {
    const duration = Date.now() - startTime;
    const logMessage = `HTTP ${status.toUpperCase()}: ${req.method} ${req.url} (${duration}ms)`;

    if (status === 'error' && error) {
      console.error(logMessage, error);
    } else {
    }

    // Log performance warnings
    if (duration > 5000) {
      console.warn(`Slow request detected: ${req.url} took ${duration}ms`);
    }
  }
};
